/**
 * Les rayons publics de l'annuaire — source unique.
 *
 * ─── L'ARCHITECTURE EN UNE PHRASE ─────────────────────────────────────────
 * Ce qui VEND se termine par `-en-ligne`. Ce qui MONTRE est au pluriel.
 * `/logiciel-association` chapeaute les trois outils.
 *
 *   VEND (aux associations)      MONTRE (aux visiteurs)
 *   ───────────────────────      ──────────────────────
 *   /logiciel-association    ↔   /associations
 *     /cagnotte     ↔   /cagnottes
 *     /billetterie-en-ligne  ↔   /evenements
 *     /adhesion     ↔   /adhesions
 *
 * Les noms de la colonne « vend » ne sont pas choisis pour l'esthétique mais
 * sur les volumes de recherche réels (SemRush,
 * docs/seo/data/semrush-keywords-universe-11445.json) :
 *   « cagnotte en ligne »    18 100 recherches/mois
 *   « logiciel association »     590/mois
 *   « logiciel billetterie »     260/mois
 *   « gestion adhérent asso »    140/mois
 * L'écart est d'un facteur 70 entre le premier et les autres : imposer une
 * symétrie parfaite des mots aurait coûté le plus gros mot-clé du marché.
 * La cohérence tient donc dans la STRUCTURE (4 niveaux identiques par rayon),
 * pas dans des noms jumeaux.
 *
 * ─── RÈGLE À NE JAMAIS ENFREINDRE ────────────────────────────────────────
 * Jamais deux pages différentes sur le même mot au singulier et au pluriel
 * (`/cagnotte` vs `/cagnottes` : une lettre d'écart, confusion garantie pour
 * les visiteurs comme pour Google). D'où le suffixe qui distingue.
 */

export const LISTING_TYPES = [
  "tout",
  "associations",
  "cagnottes",
  "evenements",
  "adhesions",
  // Boutiques (2026-08-17). Rayon NEUF : aucune URL existante n'est déplacée,
  // contrairement à la refonte du 6 août — rien à faire redigérer à Google.
  "boutiques",
  "qr-codes",
] as const;

export type ValidType = (typeof LISTING_TYPES)[number];

/** Les rayons qui ont leur propre URL canonique et sont indexables. */
export const INDEXABLE_LISTING_TYPES = [
  "associations",
  "cagnottes",
  "evenements",
  "adhesions",
  "boutiques",
] as const satisfies readonly ValidType[];

/**
 * URL canonique d'un rayon — SOURCE UNIQUE : canonicals des pages, liens de
 * pagination, onglets et sitemap en dérivent tous.
 *
 * 2026-08-11 : les rayons repassent sous /recherche/* (décision Karim). Ce
 * sont les adresses que Google a GARDÉES en index pendant les cinq jours où
 * elles redirigeaient — la refonte du 6 août n'a jamais été digérée et a
 * coûté le référencement de marque (« donoor » : position 1,2 → 11,1).
 *
 * ⚠️ Cette table DOIT rester alignée avec les fichiers de pages
 * (`app/pages/recherche/*`) ET les redirections de nuxt.config.ts. Le
 * 11 août, les pages avaient déménagé mais cette table pointait encore vers
 * les anciennes adresses : chaque page de recherche déclarait comme
 * canonical une URL qui redirigeait vers elle-même — la boucle exacte qui
 * avait maintenu /billetterie-en-ligne hors index. Attrapé en vérification
 * post-déploiement, invisible au build (pages SSR, hors garde-fou prerender).
 */
export const LISTING_BASE_PATH: Record<ValidType, string> = {
  tout: "/recherche",
  associations: "/recherche/associations",
  cagnottes: "/recherche/cagnottes",
  evenements: "/recherche/billetterie",
  adhesions: "/recherche/adhesions",
  // Rayon NEUF (2026-08-17) : `/recherche/boutiques` n'a jamais été indexé,
  // donc aucune redirection à prévoir et aucun historique à préserver.
  boutiques: "/recherche/boutiques",
  // URL courte : /qr se place au-dessus de /qr/<id> (page de partage d'un QR,
  // server/routes/qr/[id].get.ts) — /qr liste, /qr/<id> montre.
  "qr-codes": "/qr",
};

/** Nombre d'items par page de rayon. Doit rester égal à ITEMS_PER_PAGE. */
export const LISTING_ITEMS_PER_PAGE = 30;

/**
 * URL d'une page de pagination. Page 1 = l'URL nue (jamais `/page/1`, qui
 * serait un doublon de la page de base).
 *
 * Pagination par SEGMENT DE CHEMIN et non par `?page=` : les paramètres de
 * requête sont plus souvent ignorés par Google et n'accumulent pas d'autorité.
 */
export function listingPagePath(type: ValidType, page: number): string {
  const base = LISTING_BASE_PATH[type];
  return page <= 1 ? base : `${base}/page/${page}`;
}

/**
 * Sous-dossiers de /associations qui sont des LISTINGS, pas des fiches.
 * Ce sont aussi les seuls segments interdits comme slug d'organisation.
 */
const ASSOCIATION_LISTING_SEGMENTS = ["ville", "departement", "region", "page"];

/**
 * Troisièmes segments qui appartiennent à UNE association, pas à un listing
 * géographique : `/associations/<asso>/billetterie`, `/cagnottes`, etc.
 *
 * ⚠️ SANS CETTE LISTE, la règle « 3 segments sous /associations = listing
 * ville × catégorie » les attrapait tous (introduit le 2026-09-12 avec ces
 * pages) : ils héritaient du footer complet et de l'en-tête des rayons, alors
 * qu'ils sont du contenu d'association. Nuxt, lui, route déjà correctement —
 * un segment STATIQUE l'emporte sur `[categorie]`.
 */
const ASSOCIATION_FEATURE_SEGMENTS = [
  "billetterie",
  "cagnottes",
  "adhesions",
  "boutiques",
  "qr-codes",
];

/**
 * Cette URL est-elle une page de LISTING (rayon, pagination, hub géographique
 * ou outil de recherche) ?
 *
 * POURQUOI CETTE FONCTION (régression repérée par Karim au test, 2026-08-06) :
 * deux composants décidaient de leur affichage en testant le chemin à la main.
 *   - AppHeader : `path.startsWith("/recherche")` pour remplacer les liens de
 *     navigation par la searchbar. Les rayons ayant déménagé vers /associations,
 *     /cagnottes… la searchbar avait disparu de toutes les pages de listing.
 *   - app.vue : `path.startsWith("/associations/")` pour servir le footer
 *     allégé. Il attrapait déjà à tort /associations/ville/* AVANT la refonte,
 *     et attrapait désormais aussi la pagination et les hubs département /
 *     région.
 * Deux règles écrites deux fois, dérivant chacune de leur côté. Une seule ici.
 */
export function isListingPath(path: string): boolean {
  const clean = path.replace(/\/+$/, "") || "/";
  if (clean === "/recherche" || clean.startsWith("/recherche/")) return true;

  const segments = clean.split("/").filter(Boolean);
  const [first, second] = segments;
  if (!first) return false;

  // Rayons : /cagnottes, /evenements, /adhesions, /associations (+ /page/N)
  const roots = ["associations", "cagnottes", "evenements", "adhesions", "qr"];
  if (!roots.includes(first)) return false;
  if (segments.length === 1) return true;

  // /associations/<slug> est une FICHE ; /associations/ville|departement|
  // region|page/... sont des listings.
  if (first === "associations") {
    if (!second) return false;
    if (ASSOCIATION_LISTING_SEGMENTS.includes(second)) return true;
    // /associations/<asso>/billetterie… = contenu d'UNE association.
    if (
      segments.length === 3 &&
      ASSOCIATION_FEATURE_SEGMENTS.includes(segments[2] ?? "")
    ) {
      return false;
    }
    // /associations/<ville>/<categorie> = 3 segments = listing programmatique.
    return segments.length >= 3;
  }

  // /qr/<id> est la page de PARTAGE d'un QR précis (server/routes/qr/[id]),
  // pas un listing : seul /qr nu et /qr/page/N en sont.
  if (first === "qr") return second === "page";

  // /cagnottes/<ville>, /evenements/<ville>, /adhesions/<ville> et /page/N.
  return true;
}

/**
 * Cette URL est-elle la FICHE d'une association précise ?
 * (/associations/<slug>, jamais un listing.)
 */
export function isOrganizationFichePath(path: string): boolean {
  const clean = path.replace(/\/+$/, "") || "/";
  const segments = clean.split("/").filter(Boolean);
  if (segments[0] !== "associations") return false;
  // La fiche elle-même : /associations/<asso>.
  if (
    segments.length === 2 &&
    !ASSOCIATION_LISTING_SEGMENTS.includes(segments[1] ?? "")
  ) {
    return true;
  }
  // Ses pages « tout lister » : même famille, donc même pied de page allégé.
  return (
    segments.length === 3 &&
    ASSOCIATION_FEATURE_SEGMENTS.includes(segments[2] ?? "")
  );
}

/**
 * Une page de pagination demandée au-delà de la dernière doit-elle répondre
 * 404 ?
 *
 * ─── CE QUI L'A MOTIVÉ (mesuré le 2026-09-18) ────────────────────────────
 * `/recherche/associations/page/999` répondait **200, `index, follow`**, avec
 * une liste vide et un canonical pointant sur elle-même. Autrement dit : une
 * infinité d'URLs vides, toutes explorables et toutes déclarées indexables.
 * Même chose sur les rayons cagnottes, adhésions et boutiques ; seuls les
 * rayons événements et le blog renvoyaient un vrai 404.
 *
 * Pourquoi c'est grave ICI et pas sur un gros site : la Search Console montre
 * que sur 20 fiches d'associations tirées du sitemap, **9 n'ont jamais été
 * explorées** (« Détectée, actuellement non indexée » ou « Google ne reconnaît
 * pas cette URL »). Le budget d'exploration d'un domaine d'un an est minuscule
 * — chaque page vide explorée est une fiche d'association qui ne l'est pas.
 *
 * Le 404 sur page 1 et sur les numéros < 2 est déjà rendu par les fichiers de
 * route `page/[n].vue` ; ce prédicat couvre la borne HAUTE, la seule qui
 * demande de connaître le nombre réel d'items.
 *
 * @param page          Numéro demandé dans l'URL (1-indexé).
 * @param totalPages    Nombre de pages réellement disponibles (≥ 1).
 * @param donneesIndisponibles  L'API n'a pas répondu : on ne conclut RIEN.
 *   Un incident d'API ne doit jamais se traduire par des 404 en série sur des
 *   pages qui existent — Google les désindexerait pour de bon.
 */
export function estPageHorsBornes(
  page: number,
  totalPages: number,
  donneesIndisponibles = false,
): boolean {
  if (donneesIndisponibles) return false;
  if (!Number.isFinite(page) || !Number.isFinite(totalPages)) return false;
  if (page <= 1) return false;
  return page > Math.max(1, totalPages);
}
